import React from 'react';
import {
  User, Phone, MapPin, Briefcase, Award, DollarSign,
  Camera, FileText, Shield, RefreshCw,
} from 'lucide-react';
import AddressVerifier from '../AddressVerifier';

const EXPERIENCE_LEVELS = [
  { value: 'entry', label: 'Entry Level (0-2 years)' },
  { value: 'mid', label: 'Mid Level (2-5 years)' },
  { value: 'senior', label: 'Senior Level (5+ years)' },
  { value: 'expert', label: 'Expert (10+ years)' },
];

const resolvePhoto = (photo) => {
  if (photo.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  if (photo.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}/api${photo}`;
  return photo;
};

/**
 * "Edit Profile" section body for WorkerProfileSettings.
 * All state lives in the parent — this is purely presentational.
 */
export default function EditProfileSection({
  profile,
  setProfile,
  addressFields,
  onAddressChange,
  photoInputRef,
  uploadingPhoto,
  onPhotoUpload,
  saving,
  onSave,
}) {
  return (
    <div className="space-y-6">
      {/* Profile Photo */}
      <div className="flex items-center gap-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center overflow-hidden">
            {profile.profile_photo ? (
              <img src={resolvePhoto(profile.profile_photo)} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <span className="text-4xl font-bold text-white">
                {profile.full_name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            )}
            {uploadingPhoto && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
          </div>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onPhotoUpload}
            className="hidden"
            data-testid="profile-photo-input"
          />
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploadingPhoto}
            className="absolute -bottom-2 -right-2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            title="Change photo"
            data-testid="change-photo-btn"
          >
            {uploadingPhoto ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </button>
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{profile.full_name || 'Your Name'}</h3>
          <p className="text-sm text-gray-500">Click the camera icon to upload a photo</p>
          <p className="text-xs text-gray-400 mt-1">JPEG, PNG, or WebP (max 2MB)</p>
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="profile-full-name" className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-2" />
            Full Name
          </label>
          <input
            id="profile-full-name"
            type="text"
            value={profile.full_name}
            onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter your full name"
            data-testid="profile-full-name"
          />
        </div>
        <div>
          <label htmlFor="profile-phone" className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="w-4 h-4 inline mr-2" />
            Phone Number
          </label>
          <input
            id="profile-phone"
            type="tel"
            value={profile.phone}
            onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter your phone number"
            data-testid="profile-phone"
          />
        </div>
      </div>

      <div>
        <div className="block text-sm font-medium text-gray-700 mb-2">
          <MapPin className="w-4 h-4 inline mr-2" />
          Home Address
        </div>
        <AddressVerifier
          value={addressFields}
          onChange={onAddressChange}
          colorScheme="blue"
          testIdPrefix="profile-address"
        />
      </div>

      <div>
        <label htmlFor="profile-bio" className="block text-sm font-medium text-gray-700 mb-2">
          <FileText className="w-4 h-4 inline mr-2" />
          Bio / About Me
        </label>
        <textarea
          id="profile-bio"
          value={profile.bio}
          onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
          placeholder="Tell employers about yourself..."
          data-testid="profile-bio"
        />
      </div>

      {/* Professional Info */}
      <div className="pt-4 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Information</h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="profile-experience" className="block text-sm font-medium text-gray-700 mb-2">
              <Award className="w-4 h-4 inline mr-2" />
              Experience Level
            </label>
            <select
              id="profile-experience"
              value={profile.experience_level}
              onChange={(e) => setProfile({ ...profile, experience_level: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="profile-experience"
            >
              {EXPERIENCE_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>{level.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="profile-hourly-rate" className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="w-4 h-4 inline mr-2" />
              Desired Hourly Rate ($)
            </label>
            <input
              id="profile-hourly-rate"
              type="number"
              value={profile.hourly_rate}
              onChange={(e) => setProfile({ ...profile, hourly_rate: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., 25"
              min="0"
              data-testid="profile-hourly-rate"
            />
          </div>
        </div>

        <div className="mt-4">
          <label htmlFor="profile-skills" className="block text-sm font-medium text-gray-700 mb-2">
            <Briefcase className="w-4 h-4 inline mr-2" />
            Skills (comma separated)
          </label>
          <input
            id="profile-skills"
            type="text"
            value={profile.skills}
            onChange={(e) => setProfile({ ...profile, skills: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Nursing, Patient Care, First Aid"
            data-testid="profile-skills"
          />
          {profile.skills && (
            <div className="flex flex-wrap gap-2 mt-2">
              {profile.skills.split(',').filter((s) => s.trim()).map((skill) => (
                <span key={skill} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                  {skill.trim()}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4">
          <label htmlFor="profile-certifications" className="block text-sm font-medium text-gray-700 mb-2">
            <Shield className="w-4 h-4 inline mr-2" />
            Certifications (comma separated)
          </label>
          <input
            id="profile-certifications"
            type="text"
            value={profile.certifications}
            onChange={(e) => setProfile({ ...profile, certifications: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., RN, CPR, BLS"
            data-testid="profile-certifications"
          />
          {profile.certifications && (
            <div className="flex flex-wrap gap-2 mt-2">
              {profile.certifications.split(',').filter((c) => c.trim()).map((cert) => (
                <span key={cert} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                  {cert.trim()}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="pt-4">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50"
          data-testid="save-profile-btn"
        >
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}
